/**
 * Style Hooks
 * React hooks for component styling
 */

import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * Hook for panel component styles
 * 
 * Standard Panel Layout Structure:
 * - Container: No padding (flex column, full height)
 * - Alert/Info Box: mx: 2, mt: 2, mb: 1.5 (consistent margins)
 * - Content Container: p: 2, pt: 0 (padding except top to avoid double spacing)
 * - This creates consistent 2-unit spacing around content and 1.5-unit gap after alerts
 */
export const usePanelStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      ...options.root,
    },
    
    header: {
      padding: theme.spacing(1.5),
      height: theme.dimensions.panel.headerHeight,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: options.headerColor || theme.palette.grey[50],
      flexShrink: 0,
      ...options.header,
    },
    
    headerTitle: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.palette.text.primary,
      ...options.headerTitle,
    },
    
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      ...options.headerActions,
    },
    
    headerIconButton: {
      size: 'small',
      padding: theme.spacing(0.25),
    },
    
    draggableHeader: {
      padding: theme.spacing(1.5),
      height: theme.dimensions.panel.headerHeight,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: options.headerColor || theme.palette.grey[50],
      cursor: 'grab',
      flexShrink: 0,
      ...options.header,
    },
    
    content: {
      flex: 1,
      padding: theme.spacing(2),
      paddingTop: 0, // Avoid double spacing when following an alert
      overflow: 'auto',
      ...theme.mixins.scrollable,
      ...options.content,
    },
    
    // Standard alert positioning for consistency
    alertBox: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
    
    footer: {
      padding: theme.spacing(1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
      flexShrink: 0,
      ...options.footer,
    },
  }), [theme, options]);
};

/**
 * Hook for dialog component styles
 */
export const useDialogStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    paper: {
      width: options.width || 'fit-content',
      minWidth: options.minWidth || theme.spacing(50), // Scale with density
      maxWidth: options.maxWidth || '90vw',
      height: options.height || 'auto',
      minHeight: options.minHeight || theme.spacing(37.5), // Scale with density
      maxHeight: options.maxHeight || '80vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      ...options.paper,
    },
    
    header: {
      padding: theme.spacing(1.5),
      backgroundColor: options.headerColor || theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: options.draggable ? 'grab' : 'default',
      ...options.header,
    },
    
    title: {
      fontSize: theme.typography.fontSize.md,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.palette.text.primary,
      ...options.title,
    },
    
    content: {
      flex: 1,
      padding: theme.spacing(2),
      overflow: 'auto',
      ...theme.mixins.scrollable,
      ...options.content,
    },
    
    actions: {
      padding: theme.spacing(1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      ...options.actions,
    },
  }), [theme, options]);
};

/**
 * Hook for form field styles
 */
export const useFormFieldStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    field: {
      width: '100%',
      marginBottom: theme.spacing(2),
      ...options.field,
    },
    
    label: {
      display: 'block',
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      ...options.label,
    },
    
    input: {
      fontSize: theme.typography.fontSize.base,
      '& .MuiInputBase-root': {
        fontSize: theme.typography.fontSize.base,
      },
      '& .MuiInputLabel-root': {
        fontSize: theme.typography.fontSize.base,
      },
      ...options.input,
    },
    
    helperText: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      ...options.helperText,
    },
    
    error: {
      '& .MuiInputBase-root': {
        borderColor: theme.palette.error.main,
      },
      '& .MuiFormHelperText-root': {
        color: theme.palette.error.main,
      },
      ...options.error,
    },
  }), [theme, options]);
};

/**
 * Hook for card component styles
 */
export const useCardStyles = (options = {}) => {
  const theme = useTheme();
  const isSelected = options.selected;
  
  return useMemo(() => ({
    root: {
      border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
      backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.08)' : theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
      cursor: options.clickable ? 'pointer' : 'default',
      '&:hover': options.clickable ? {
        borderColor: theme.palette.primary.light,
        backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.12)' : theme.palette.action.hover,
      } : {},
      ...options.root,
    },
    
    content: {
      padding: theme.spacing(1.5),
      '&:last-child': {
        paddingBottom: theme.spacing(1.5),
      },
      ...options.content,
    },
    
    title: {
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      marginBottom: theme.spacing(0.5),
      ...options.title,
    },
    
    subtitle: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
      ...options.subtitle,
    },
    
    body: {
      fontSize: theme.typography.fontSize.base,
      color: theme.palette.text.primary,
      ...options.body,
    },
  }), [theme, options, isSelected]);
};

/**
 * Hook for button styles
 */
export const useButtonStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    root: {
      fontSize: theme.typography.fontSize.sm,
      textTransform: 'none',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1),
      height: options.size === 'large' ? theme.dimensions.button.large : options.size === 'small' ? theme.dimensions.button.small : theme.dimensions.button.medium,
      ...options.root,
    },
    
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      ...options.primary,
    },
    
    secondary: {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
      ...options.secondary,
    },
    
    outlined: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main,
      },
      ...options.outlined,
    },
    
    text: {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      ...options.text,
    },
  }), [theme, options]);
};

/**
 * Hook for grid styles
 */
export const useGridStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    root: {
      width: '100%',
      height: '100%',
      '& .MuiDataGrid-root': {
        fontSize: theme.typography.fontSize.base,
        border: 'none',
      },
      '& .MuiDataGrid-cell': {
        fontSize: theme.typography.fontSize.base,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: theme.typography.fontSize.base,
        fontWeight: theme.typography.fontWeight.semibold,
      },
      '& .MuiDataGrid-footerContainer': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      ...options.root,
    },
    
    row: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(33, 150, 243, 0.08)',
        '&:hover': {
          backgroundColor: 'rgba(33, 150, 243, 0.12)',
        },
      },
      ...options.row,
    },
    
    cell: {
      fontSize: theme.typography.fontSize.base,
      ...options.cell,
    },
  }), [theme, options]);
};

/**
 * Hook for alert styles
 */
export const useAlertStyles = (options = {}) => {
  const theme = useTheme();
  
  return useMemo(() => ({
    root: {
      padding: theme.spacing(0.5, 2),
      marginBottom: theme.spacing(2),
      fontSize: theme.typography.fontSize.base,
      '& .MuiAlert-message': {
        fontSize: theme.typography.fontSize.base,
        padding: theme.spacing(0.5, 0),
      },
      '& .MuiAlert-icon': {
        fontSize: theme.typography.fontSize.xl,
        marginRight: theme.spacing(1),
      },
      ...options.root,
    },
    
    compact: {
      padding: theme.spacing(0.25, 1.5),
      marginBottom: theme.spacing(1),
      '& .MuiAlert-message': {
        fontSize: theme.typography.fontSize.sm,
      },
      '& .MuiAlert-icon': {
        fontSize: theme.typography.fontSize.lg,
      },
      ...options.compact,
    },
  }), [theme, options]);
};

/**
 * ==================================================
 * COMPATIBILITY LAYER FOR MIGRATION FROM panelStyles.js

/**
 * Helper function to apply text field styles with modifiers
 * Compatible with theme-based styling
 */
export const getTextFieldProps = (variant = 'standard', modifiers = {}) => {
  const baseProps = {
    size: 'small',
    fullWidth: true,
    InputProps: {
      sx: {
        fontSize: (theme) => theme.typography.fontSize.base || '0.75rem',
        ...(modifiers.varied && {
          fontStyle: 'italic',
          color: 'text.secondary'
        }),
        ...(modifiers.modified && {
          fontWeight: (theme) => theme.typography.fontWeight.semibold
        })
      }
    },
    InputLabelProps: {
      shrink: true,
      sx: {
        fontSize: (theme) => theme.typography.fontSize.base || '0.75rem'
      }
    },
    FormHelperTextProps: {
      sx: {
        fontSize: (theme) => theme.typography.fontSize.sm || '0.7rem'
      }
    },
    sx: {
      mb: (theme) => theme.spacing(1)
    },
    ...(modifiers.multiline && {
      multiline: true,
      rows: 3
    })
  };
  
  return baseProps;
};

/**
 * Hook for menu styles
 * Provides minimal, density-aware styling for menus
 */
export const useMenuStyles = () => {
  const theme = useTheme();
  
  return useMemo(() => ({
    // Main button trigger - minimal overrides
    trigger: {
      gap: theme.spacing(1),  // Fix spacing between icon and text
      textTransform: 'none',  // Keep text normal case
      color: theme.palette.text.secondary,  // Use gray color like other components
    },
    
    // Menu paper - density-aware width with padding
    paper: {
      minWidth: theme.spacing(35),  // Scales with density
      maxWidth: theme.spacing(45),  // Prevents content cropping
      py: theme.spacing(0.5),  // Add vertical padding for better spacing
    },
    
    // Section divider with proper spacing
    divider: {
      my: theme.spacing(0.5),
    },
    
    // Nested list container for submenus
    nestedList: {
      pl: theme.spacing(2),
      // Add spacing to prevent items from touching dividers
      '& .MuiDivider-root': {
        my: theme.spacing(0.5),
      },
    },
  }), [theme]);
};

/**
 * Hook for section header styles
 * Used for consistent section headers throughout the app
 * 
 * SECTION STRUCTURE PATTERN:
 * Always wrap sections in a container that handles spacing via gap:
 * 
 * <Box sx={sectionStyles.sectionContainer}>
 *   <Typography sx={sectionStyles.sectionHeader}>SECTION TITLE</Typography>
 *   <Box>Section content here</Box>
 * </Box>
 * 
 * The container's gap property handles spacing between header and content.
 * DO NOT add margin-bottom to headers or margin-top to content.
 * 
 * Common patterns:
 * - sectionHeader: Main section titles (uppercase, semibold)
 * - subsectionHeader: Sub-section titles  
 * - sectionContainer: Wrapper with gap spacing (handles all spacing)
 * - sectionDivider: Divider between subsections
 * - instructionText: User instructions
 * - helperText: Secondary/helper text
 */
export const useSectionStyles = () => {
  const theme = useTheme();
  
  return useMemo(() => ({
    // Main section headers (e.g., "RESIDUE SELECTION", "CONFIGURATIONS")
    sectionHeader: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      display: 'block'
    },
    
    // Sub-section headers (e.g., "Create New with Cutoff Rule")
    subsectionHeader: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      display: 'block',
      mb: theme.spacing(0.5)
    },
    
    // Section container with consistent spacing
    sectionContainer: {
      mb: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    
    // Section divider - use between subsections within a container
    // Example: <Divider sx={sectionStyles.sectionDivider} />
    sectionDivider: {
      my: theme.spacing(0.5)
    },
    
    // Instruction text (e.g., "Click field to add to pattern:")
    instructionText: {
      fontSize: theme.typography.fontSize.base,
      lineHeight: 1.4,
      mb: theme.spacing(1),
      display: 'block',
      color: theme.palette.text.primary
    },
    
    // Helper/secondary text
    helperText: {
      fontSize: theme.typography.fontSize.sm,  // Changed from xs to sm for better readability
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
      display: 'block'
    },
    
    // Code blocks with background
    codeBlock: {
      fontSize: theme.typography.fontSize.sm,  // Changed from xs to sm for better readability
      fontFamily: theme.typography.fontFamilyMono || '"Roboto Mono", "Courier New", monospace',
      backgroundColor: theme.palette.grey[100],
      px: theme.spacing(1),
      py: theme.spacing(0.5),
      borderRadius: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      display: 'block',
      width: '100%',
      overflow: 'auto'
    },
    
    // Inline code without background
    codeInline: {
      fontSize: theme.typography.fontSize.xs,
      fontFamily: theme.typography.fontFamilyMono || '"Roboto Mono", "Courier New", monospace',
      color: theme.palette.text.secondary,
      display: 'inline-block'
    }
  }), [theme]);
};